import React from 'react';
import { Box, Paper, Typography, Button, Stack, Chip } from '@mui/material';
import { Lock as LockIcon, AutoAwesome as SparkleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCurrentPlan } from '../hooks/useCurrentPlan';

interface UpgradeGateProps {
  feature: 'crm' | 'superAdmin';
  featureLabel?: string; // e.g. "Client Management", "AI Assistant"
  children: React.ReactNode;
}

export const UpgradeGate: React.FC<UpgradeGateProps> = ({
  feature,
  featureLabel = 'this feature',
  children,
}) => {
  const navigate = useNavigate();
  const { data: plan, isLoading } = useCurrentPlan();

  // While loading, render children to avoid a flash of the lock overlay for entitled users.
  if (isLoading) return <>{children}</>;

  const isEntitled =
    feature === 'crm' ? plan?.crmEnabled === true : plan?.superAdminEnabled === true;

  if (isEntitled) return <>{children}</>;

  const requiredPlans = feature === 'crm' ? 'Business and Business Plus' : 'Business Plus';

  return (
    <Box sx={{ position: 'relative', minHeight: '60vh' }}>
      <Box
        aria-hidden
        sx={{
          filter: 'blur(6px)',
          pointerEvents: 'none',
          userSelect: 'none',
          opacity: 0.55,
        }}
      >
        {children}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            maxWidth: 440,
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              borderRadius: '50%',
              backgroundColor: 'primary.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundImage: 'linear-gradient(135deg, #e3f2fd, #bbdefb)',
            }}
          >
            <LockIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          </Box>
          <Chip
            icon={<SparkleIcon sx={{ fontSize: 14 }} />}
            label={feature === 'crm' ? 'CRM module' : 'SuperAdmin'}
            color="primary"
            size="small"
            sx={{ mb: 1, fontWeight: 600 }}
          />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Upgrade to unlock {featureLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Available on {requiredPlans}. Upgrade your plan to use this feature.
          </Typography>
          {plan?.planName && (
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              You are currently on the <strong>{plan.planName}</strong> plan.
            </Typography>
          )}
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/pricing')}
            >
              See plans
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate(-1)}>
              Maybe later
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default UpgradeGate;
